import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ICompetency, ICompetencyInput } from "models/competency";
import React, { useState } from "react";
import { useMutation } from "react-query";

import {
  CompetencyCells,
  addCompetency,
  deleteCompetency,
  editCompetency,
  getCompetencies,
  importCompetency,
} from "framework/competencies";
import AddCompetency from "./partials/AddCompetency/AddCompetency";
import EditCompetency from "./partials/EditCompetency/EditCompetency";
import DeleteCompetency from "./partials/DeleteCompetency/DeleteCompetency";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
import { UserRoleName } from "types/auth";
interface Props {
  feature: Feature;
}

const Competencies: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);

  const [FilterCompetencies, setFilterCompetencies] = useState<ICompetency[]>(
    [] as ICompetency[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompetency, setSelectedCompetency] = useState<ICompetency>(
    {} as ICompetency
  );
  const [inputObj, setInputObj] = useState<ICompetencyInput>(
    {} as ICompetencyInput
  );
  const user = getUserData();
  const { colors, roleName } = user;
  const { setAlert } = useAlert();

  const addCompetenciesMutation = useMutation({
    mutationFn: (createInput: ICompetencyInput) => {
      return addCompetency(createInput);
    },
  });



  const editCompetenciesMutation = useMutation({
    mutationFn: (data: { createInput: ICompetencyInput; id: string }) => {
      return editCompetency(data.createInput, data.id);
    },
  });

  const deleteCompetenciesMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteCompetency(code);
    },
  });

  const {
    loading,
    data: Competencies,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompetencies(),
    fetchPermission: () => getPermission(8),
    section: "Competencies",
    onSuccess: (data) => setFilterCompetencies(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Competency: ICompetency) => {
    setSelectedCompetency(Competency);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompetency({} as ICompetency);
  };

  const handleOpenDelete = (Competency: ICompetency) => {
    setSelectedCompetency(Competency);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompetency({} as ICompetency);
  };

  const getBodyCells = (row: ICompetency) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title="Edit">
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addCompetenciesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Competency added successfully",
        type: "success",
      });
      revalidate();
      setInputObj({} as ICompetencyInput);
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: ICompetencyInput = {
        name: inputObj.name || selectedCompetency.name,
        nameAr: inputObj.nameAr || selectedCompetency.nameAr,
      };
      await editCompetenciesMutation.mutateAsync({
        createInput,
        id: selectedCompetency.id,
      });
      setAlert({
        open: true,
        message: "Competency updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ICompetencyInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };





  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCompetenciesMutation.mutateAsync(selectedCompetency.id);
      setAlert({
        open: true,
        message: "Competency deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompetencies(Competencies!);
    let filtered = Competencies!.filter((city) =>
      city.name?.toLowerCase()?.includes(value.toLowerCase()) || city.nameAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterCompetencies(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Competency">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCompetencies.length > 0 && (
          <List
            headCells={CompetencyCells}
            data={FilterCompetencies}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCompetencies || FilterCompetencies.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon />}
              header="No Competencies Found"
              message="There are no Competencies available"
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompetency
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCompetency
          open={openEdit}
          loading={loading2}
          competency={selectedCompetency}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCompetency
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Competencies;
